import { useState } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';

import { T } from '../../util/Localize';
import Api from '../../util/Api';
import { TextField } from '../../components/input';
import { PHONE_MAX_LENGTH } from '../clients/client-contact-information';
import { REFERRAL_CODE_LENGTH } from '../../services/registration';
import { AuthPageLayout } from './auth-page-layout';
import { authSubmitButtonSx } from './auth-submit-button';
import { postLoginDestination } from './post-login-destination';
import type { User } from '../../types/api';

const HEADING = 'Almost there.';
const BODY =
    "We've got your name and email from Google. Just a few more details and you're all set.";

export interface GoogleSignUpCompletionProps {
    /** The account the Google sign-up just created, already signed in. */
    user: User | undefined;
}

/**
 * UX-806 — shown once, straight after a Google sign-up creates a new account (`sso=created` on
 * `/ux/register`, `hub-register-page.tsx`). Google gives us the name, email and photo but not the
 * brokerage or phone the standard form asks for, so the user fills those in here before going on.
 *
 * Only the phone is saved, with the client's own `PATCH clients/{id}` (phone is one of the API's
 * `SelfEditableFields`). Brokerage and referral code are collected but deliberately NOT saved
 * (product decision, 2026-10-29): there is no field for either on an existing client.
 *
 * Leaving without submitting is fine: the account already exists, and a later sign-in goes
 * straight to the dashboard — the page is never shown again, because the `created` outcome only
 * exists on the load that came back from Google.
 */
export function GoogleSignUpCompletion({ user }: GoogleSignUpCompletionProps) {
    const [companyName, setCompanyName] = useState('');
    const [phone, setPhone] = useState('');
    const [referralCode, setReferralCode] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const canSubmit = companyName.trim().length > 0 && phone.trim().length > 0;

    const onSubmit = async () => {
        if (!canSubmit || submitting) return;
        setError(null);
        setSubmitting(true);
        const response = user?.userId
            ? await Api.patch(`clients/${user.userId}`, { phone: phone.trim() })
            : undefined;
        if (!response?.ok) {
            setSubmitting(false);
            setError(T('Something went wrong. Please try again.'));
            return;
        }
        // Stays in progress through the navigation.
        window.location.replace(postLoginDestination(user));
    };

    return (
        <AuthPageLayout>
            <Box display="flex" flexDirection="column" gap={4}>
                <Box>
                    <Typography
                        component="h1"
                        sx={{
                            color: 'inherit',
                            fontWeight: 900,
                            fontSize: { xs: 27, sm: 36 },
                            lineHeight: 1
                        }}
                    >
                        {T(HEADING)}
                    </Typography>
                    <Typography sx={{ color: 'inherit', fontSize: 14, fontWeight: 500, mt: 1 }}>
                        {T(BODY)}
                    </Typography>
                </Box>
                <Box
                    component="form"
                    display="flex"
                    flexDirection="column"
                    gap={1.25}
                    onSubmit={(e: React.FormEvent) => {
                        e.preventDefault();
                        void onSubmit();
                    }}
                >
                    <TextField
                        label={T('Brokerage/Company Name')}
                        required
                        labelSx={{ color: 'inherit' }}
                        disabled={submitting}
                        value={companyName}
                        onChange={e => setCompanyName(e.target.value)}
                    />
                    <Box display="flex" gap={1.25}>
                        <Box flex={1} minWidth={0}>
                            <TextField
                                label={T('Phone Number')}
                                required
                                type="tel"
                                labelSx={{ color: 'inherit' }}
                                disabled={submitting}
                                value={phone}
                                maxLength={PHONE_MAX_LENGTH}
                                onChange={e => setPhone(e.target.value)}
                            />
                        </Box>
                        <Box flex={1} minWidth={0}>
                            <TextField
                                label={
                                    <>
                                        {T('Referral Code')}{' '}
                                        <Box component="span" sx={{ fontSize: 11 }}>
                                            {T('(optional)')}
                                        </Box>
                                    </>
                                }
                                labelSx={{ color: 'inherit' }}
                                disabled={submitting}
                                value={referralCode}
                                // Digits only, capped at the code length — as on the registration form.
                                onChange={e =>
                                    setReferralCode(
                                        e.target.value
                                            .replace(/\D/g, '')
                                            .slice(0, REFERRAL_CODE_LENGTH)
                                    )
                                }
                                inputProps={{
                                    inputMode: 'numeric',
                                    maxLength: REFERRAL_CODE_LENGTH
                                }}
                            />
                        </Box>
                    </Box>
                    {error && (
                        <Typography role="alert" sx={{ color: '#ff8a80', fontSize: 13 }}>
                            {error}
                        </Typography>
                    )}
                    <Button
                        type="submit"
                        variant="contained"
                        fullWidth
                        disabled={!canSubmit || submitting}
                        sx={authSubmitButtonSx}
                    >
                        {submitting ? (
                            <CircularProgress size={22} sx={{ color: 'inherit' }} />
                        ) : (
                            T('Continue to Account')
                        )}
                    </Button>
                </Box>
            </Box>
        </AuthPageLayout>
    );
}
